import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SLOT_BY_ID, type ReconSummary, type SlotState } from "@ranfac/shared";
import { fetchRecons, fetchUploads } from "../api.js";
import { ProgressTracker } from "../components/ProgressTracker.js";

export function Dashboard() {
  const { period = "2026-01" } = useParams();
  const [recons, setRecons] = useState<ReconSummary[] | null>(null);
  const [slots, setSlots] = useState<SlotState[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    const [y, m] = period.split("-");
    const periodEnd = new Date(Date.UTC(Number(y), Number(m), 0))
      .toISOString()
      .slice(0, 10);
    Promise.all([
      fetch(`${import.meta.env.VITE_API_BASE ?? ""}/api/recons?periodEnd=${periodEnd}`, { credentials: "include" }).then((r) => r.json()),
      fetchUploads(period),
    ])
      .then(([r, u]) => {
        setRecons(r.recons);
        setSlots(u.slots);
      })
      .catch((e) => setErr(String(e)));
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadedSlotIds = new Set(
    (slots ?? []).filter((s) => s.status === "uploaded").map((s) => s.slotId),
  );

  function reconIsBlocked(reconId: string): string[] {
    const blocking: string[] = [];
    for (const def of Object.values(SLOT_BY_ID)) {
      if (
        def.feedsRecons.includes(reconId) &&
        !uploadedSlotIds.has(def.id)
      ) {
        blocking.push(def.name);
      }
    }
    return blocking;
  }

  const uploadsDone = (slots ?? []).filter((s) => s.status === "uploaded")
    .length;
  const uploadsTotal = Object.values(SLOT_BY_ID).length;
  const reconsReady = (recons ?? []).filter(
    (r) => reconIsBlocked(r.id).length === 0,
  ).length;
  const reconsTotal = (recons ?? []).length;

  return (
    <>
      <ProgressTracker
        period={period}
        uploadsDone={uploadsDone}
        uploadsTotal={uploadsTotal}
        reconsDone={0}
        reconsTotal={30}
        signaturesDone={0}
        signaturesTotal={30}
        binderLocked={false}
      />

      <div className="card">
        <h1>Close — {prettyPeriod(period)}</h1>
        <div className="subtle">
          Upload the Sage source files, work through each reconciliation, route
          for review, and lock the period.
        </div>
      </div>

      {uploadsDone < uploadsTotal && (
        <div className="banner warn" style={{ marginBottom: 20 }}>
          <strong>Start here:</strong> {uploadsDone} of {uploadsTotal} source
          files uploaded. Reconciliations stay locked until their required files
          are in.{" "}
          <Link to={`/close/${period}/sources`}>Go to Source Files →</Link>
        </div>
      )}

      <div className="kpi-grid">
        <Kpi label="Source files" value={`${uploadsDone}/${uploadsTotal}`} />
        <Kpi label="Recons ready" value={`${reconsReady}/${reconsTotal}`} />
        <Kpi label="In review" value={count(recons, "in_review")} />
        <Kpi label="Signed & locked" value={count(recons, "signed")} />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Reconciliations</h2>
          <Link className="link-btn" to={`/close/${period}/sources`}>
            Manage source files →
          </Link>
        </div>
        {err && <div className="banner warn">Could not load: {err}</div>}
        <table>
          <thead>
            <tr>
              <th>Workpaper</th>
              <th>Phase</th>
              <th>GL</th>
              <th>Preparer</th>
              <th>Reviewer</th>
              <th>Status</th>
              <th>Blocked by</th>
            </tr>
          </thead>
          <tbody>
            {(recons ?? []).map((r) => {
              const blocking = reconIsBlocked(r.id);
              const ready = blocking.length === 0;
              const hrefs: Record<string, string> = {
                "po-accrual": `/close/${period}/recons/po-accrual`,
                "accrued-vacation": `/close/${period}/recons/accrued-vacation`,
                "inventory": `/close/${period}/recons/inventory`,
                "fixed-assets": `/close/${period}/recons/fixed-assets`,
                "lease-rou": `/close/${period}/recons/lease-rou`,
                "intercompany-cervos": `/close/${period}/recons/intercompany-cervos`,
              };
              const href = hrefs[r.id];
              return (
                <tr key={r.id} className={ready ? "" : "row-blocked"}>
                  <td>
                    {href ? (
                      <Link to={href}>{r.name}</Link>
                    ) : (
                      <span>{r.name}</span>
                    )}
                  </td>
                  <td>{r.phase}</td>
                  <td className="monoish">{r.glAccounts.join(", ")}</td>
                  <td>{r.preparer ?? "—"}</td>
                  <td>{r.reviewer ?? "—"}</td>
                  <td>
                    <span className={`badge ${r.status}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="subtle">
                    {ready ? "—" : blocking.join(", ")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

function count(recons: ReconSummary[] | null, status: string): number | "—" {
  if (!recons) return "—";
  return recons.filter((r) => r.status === status).length;
}

function prettyPeriod(period: string): string {
  const [y, m] = period.split("-");
  const d = new Date(`${y}-${m}-01T00:00:00Z`);
  return d.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
